import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMusic } from "@/contexts/MusicContext";
import { toast } from "sonner";
import { AppRole } from "@/types/supabase";
import { supabase } from "@/integrations/supabase/client";

interface UseVisitorStreamLimitProps {
  isPlaying: boolean;
  user: any;
  userRole: AppRole | null;
  trackId: string;
}

export function useVisitorStreamLimit({
  isPlaying,
  user,
  userRole,
  trackId,
}: UseVisitorStreamLimitProps) {
  const { setIsPlaying, setCurrentTrack } = useMusic();
  const navigate = useNavigate();
  const [visitorLimitReached, setVisitorLimitReached] = useState(false);
  const [streamDuration, setStreamDuration] = useState(0);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  // Handle visitor stream limits (29 second preview)
  const playTimer = useRef<NodeJS.Timeout | null>(null);

  // Determine if this user should have limited streaming
  // Apply limit for visitors AND authenticated users without a role
  const shouldApplyLimit = !user || !userRole;

  // Reset state when track changes
  useEffect(() => {
    console.log("🔄 Resetting visitor limit state for track:", trackId);
    setVisitorLimitReached(false);
    setStreamDuration(0);

    if (playTimer.current) {
      clearInterval(playTimer.current);
      playTimer.current = null;
    }
  }, [trackId]);

  // Handle visitor/no-role user playback timing
  useEffect(() => {
    if (shouldApplyLimit && isPlaying && !visitorLimitReached) {
      console.log("⏱️ Starting preview playback timer for track:", trackId, {
        user: !!user,
        userRole,
      });
      playTimer.current = setInterval(() => {
        setStreamDuration((prev) => {
          const newDuration = prev + 1;
          console.log(
            `⏰ Preview stream duration: ${newDuration}s for track:`,
            trackId,
          );

          // Check if preview limit reached (29 seconds)
          if (newDuration >= 29) {
            console.log("🚫 Preview limit reached, stopping playback");
            if (playTimer.current) {
              clearInterval(playTimer.current);
              playTimer.current = null;
            }

            setVisitorLimitReached(true);
            setIsPlaying(false);

            // Determine redirect target and message based on user state
            if (user && !userRole) {
              // OAuth user without role - redirect to account type selection
              toast.info(
                "29-second preview limit reached - Select your account type to continue!",
              );
              setTimeout(() => {
                setCurrentTrack(null);
                navigate("/select-account-type");
              }, 500);
            } else {
              // Anonymous visitor - record this preview toward Trending Today, then show preview limit dialog
              supabase.functions
                .invoke("record-visitor-stream", {
                  body: { track_id: trackId },
                })
                .catch((err) => {
                  console.warn("Failed to record visitor stream:", err);
                });
              setShowPreviewDialog(true);
            }
          }

          return newDuration;
        });
      }, 1000);
    } else if (
      (!isPlaying || !shouldApplyLimit || visitorLimitReached) &&
      playTimer.current
    ) {
      console.log("⏹️ Stopping preview playback timer");
      clearInterval(playTimer.current);
      playTimer.current = null;
    }

    return () => {
      if (playTimer.current) {
        clearInterval(playTimer.current);
        playTimer.current = null;
      }
    };
  }, [
    isPlaying,
    shouldApplyLimit,
    visitorLimitReached,
    trackId,
    user,
    userRole,
    setIsPlaying,
    setCurrentTrack,
    navigate,
  ]);

  const dismissPreviewDialog = () => {
    setShowPreviewDialog(false);
    setCurrentTrack(null);
  };

  return {
    visitorLimitReached,
    streamDuration: shouldApplyLimit ? streamDuration : 0,
    showPreviewDialog,
    dismissPreviewDialog,
  };
}
